import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';

// Documented runtime-prop exception (slice-22): ChannelLogoWrapper takes four
// per-call-site dimension props (logoWidth/logoHeight/logoPadding/logoBorderRadius)
// that vary discretely across consumers (PlanDetailsModal, ChannelListSideModal,
// CartAddOnsContent, TVChannelLogos). A Card/Box variant has no slot for arbitrary
// runtime numeric props, so this stays a styled(Box) with shouldForwardProp.
// It carries ZERO theme.novus reads and ZERO hex: the outline base colour and the
// hover colour are sourced from the semantic palette (border.channelLogo /
// border.channelLogoHover).
const channelLogoCustomProps = new Set(['logoWidth', 'logoHeight', 'logoPadding', 'logoBorderRadius']);

export const ChannelLogoWrapper = styled(Box, {
  shouldForwardProp: (prop) => !channelLogoCustomProps.has(prop as string),
})<{
  theme?: Theme;
  logoWidth?: number | string;
  logoHeight?: number | string;
  logoPadding?: string;
  logoBorderRadius?: string;
}>(({ theme, logoWidth = 56, logoHeight = 45, logoPadding, logoBorderRadius = '5px' }) => ({
  width: logoWidth,
  height: logoHeight,
  borderRadius: logoBorderRadius,
  cursor: 'pointer',
  padding: logoPadding,
  outline: `1.5px solid ${theme?.palette.border.channelLogo}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'outline-color 0.2s ease',
  '&:hover': {
    outlineColor: theme?.palette.border.channelLogoHover,
  },
}));

// Documented exception (certified per slice-21b AC): a shared Tooltip
// componentsProps object consumed by six call sites. Its tooltip surface styling
// is a theme-callback sx so every value is token-driven — bg/text from the semantic
// palette, the drop shadow from surface.channelTooltipShadow, the 20px radius from
// shape.card. R16: the fixed 378px width is clamped to the viewport on mobile so the
// hover card never overflows a narrow screen, while keeping the 378px desktop intent.
// componentsProps (not slotProps) is retained: the installed @mui/material is pinned
// only to ^5.x and the existing API renders byte-identically.
export const channelTooltipProps: TooltipProps['componentsProps'] = {
  tooltip: {
    sx: (theme) => ({
      bgcolor: (theme as Theme).palette.surface.white,
      color: (theme as Theme).palette.border.black,
      boxShadow: (theme as Theme).palette.surface.channelTooltipShadow,
      borderRadius: (theme as Theme).shape.card,
      width: 378,
      maxWidth: 'min(378px, calc(100vw - 32px))',
      p: 0,
    }),
  },
  // The global MuiTooltip override colours the arrow borderBlack to match the default
  // dark tooltip. This tooltip overrides its surface to white, so the arrow must follow
  // the same white token — otherwise it renders as the inherited black tip.
  arrow: {
    sx: (theme) => ({
      color: (theme as Theme).palette.surface.white,
    }),
  },
};
